import React, { useState } from 'react';
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { supabase } from './supabaseClient';
import { sendAccountActivityNotification } from './accountActivityNotification';
import WingitLogo from './components/WingitLogo';

export default function JoinOrganisationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const invitationOrganisationCode = String(
    searchParams.get('organisationCode') ||
      searchParams.get('code') ||
      ''
  )
    .trim()
    .toUpperCase();

  const invitationEmail = String(
    searchParams.get('email') || ''
  ).trim();

  const [form, setForm] = useState({
    fullName: '',
    email: invitationEmail,
    password: '',
    confirmPassword: '',
    organisationCode: invitationOrganisationCode,
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const hasInvitationCode = Boolean(
    invitationOrganisationCode
  );

  function handleChange(e) {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        name === 'organisationCode'
          ? value.toUpperCase()
          : value,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!form.fullName.trim()) {
      return setError('Please enter your full name.');
    }

    if (!form.email.trim()) {
      return setError('Please enter your email address.');
    }

    if (!form.password) {
      return setError('Please enter a password.');
    }

    if (form.password !== form.confirmPassword) {
      return setError('Passwords do not match.');
    }

    if (!form.organisationCode.trim()) {
      return setError(
        'Please enter your organisation code.'
      );
    }

    setLoading(true);

    try {
      const code = form.organisationCode
        .trim()
        .toUpperCase();

      const { data: ownerOrg, error: orgError } =
        await supabase
          .rpc('lookup_organisation_by_code', {
            input_code: code,
          })
          .maybeSingle();

      if (orgError) throw orgError;

      if (!ownerOrg?.organisation_id) {
        console.log('Organisation lookup:', ownerOrg);
        setLoading(false);

        return setError(
          'Organisation code not recognised. Please check the code with your organisation owner.'
        );
      }

      const { error: signUpError } =
        await supabase.auth.signUp({
          email: form.email.trim(),
          password: form.password,
          options: {
            data: {
              full_name: form.fullName.trim(),
              org_name: ownerOrg.org_name,
              home_airfield: ownerOrg.home_airfield,
              organisation_code:
                ownerOrg.organisation_code,
              organisation_id:
                ownerOrg.organisation_id,
              role: 'member',
            },
          },
        });

      if (signUpError) throw signUpError;

      const {
  data: loginData,
  error: loginError,
} = await supabase.auth.signInWithPassword({
  email: form.email.trim(),
  password: form.password,
});

if (loginError) throw loginError;

await sendAccountActivityNotification(
  loginData.session?.access_token,
  'member_joined'
);

setLoading(false);
navigate('/dashboard');
    } catch (err) {
      setLoading(false);

      setError(
        err.message || 'Could not join organisation.'
      );
    }
  }

  return (
    <PublicShell
      rightActionLabel="Log in"
      onRightAction={() => navigate('/login')}
    >
      <main style={styles.hero}>
        <h1 style={styles.title}>
          Join organisation.
        </h1>

        <p style={styles.subtitle}>
          Create your personal account using the
          organisation code provided by your owner or
          administrator.
        </p>

        <div style={styles.panel}>
          <form
            onSubmit={handleSubmit}
            style={styles.form}
          >
            <Field label="Full name">
              <input
                name="fullName"
                placeholder="e.g. Bill Watson"
                value={form.fullName}
                onChange={handleChange}
                style={styles.input}
                autoComplete="name"
              />
            </Field>

            <Field label="Email address">
              <input
                name="email"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                style={styles.input}
                autoComplete="email"
              />
            </Field>

            <Field label="Password">
              <input
                name="password"
                type="password"
                placeholder="Create a password"
                value={form.password}
                onChange={handleChange}
                style={styles.input}
                autoComplete="new-password"
              />
            </Field>

            <Field label="Confirm password">
              <input
                name="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={form.confirmPassword}
                onChange={handleChange}
                style={styles.input}
                autoComplete="new-password"
              />
            </Field>

            <Field label="Organisation code">
              <input
                name="organisationCode"
                placeholder="e.g. WNG-7K4M2"
                value={form.organisationCode}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(hasInvitationCode
                    ? styles.invitationCodeInput
                    : {}),
                }}
                readOnly={hasInvitationCode}
                aria-readonly={hasInvitationCode}
              />

              <div style={styles.helper}>
                {hasInvitationCode
                  ? 'Your organisation code has been added automatically from your invitation.'
                  : 'Your organisation owner will provide this code.'}
              </div>
            </Field>

            {error && (
              <div style={styles.error}>{error}</div>
            )}

            <button
              type="submit"
              style={styles.primaryButton}
              disabled={loading}
            >
              {loading
                ? 'Joining...'
                : 'Create account and join'}
            </button>
          </form>
        </div>

        <div style={styles.footer}>
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/login')}
            style={styles.footerLink}
          >
            Log in
          </button>
        </div>
      </main>
    </PublicShell>
  );
}

function PublicShell({
  children,
  rightActionLabel,
  onRightAction,
}) {
  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <WingitLogo size="small" />

        <button
          type="button"
          onClick={onRightAction}
          style={styles.topButton}
        >
          {rightActionLabel}
        </button>
      </div>

      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}</span>
      {children}
    </label>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background:
      'radial-gradient(circle at top, rgba(85,199,255,0.08), transparent 34%), linear-gradient(180deg, #06111f, #050914 70%)',
    color: '#ffffff',
    padding: '1.25rem',
  },

  topBar: {
    maxWidth: 980,
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
  },

  topButton: {
    border: '1px solid rgba(85,199,255,0.24)',
    background: 'rgba(255,255,255,0.025)',
    color: '#d7ebff',
    borderRadius: 10,
    padding: '0.55rem 0.9rem',
    fontWeight: 700,
    cursor: 'pointer',
  },

  hero: {
    maxWidth: 620,
    margin: '4.2rem auto 0',
    textAlign: 'center',
  },

  title: {
    margin: 0,
    color: '#ffffff',
    fontSize: '2.4rem',
    fontWeight: 800,
    letterSpacing: '-0.04em',
  },

  subtitle: {
    maxWidth: 580,
    margin: '1rem auto 0',
    color: '#b6cbe3',
    fontSize: '1.05rem',
    lineHeight: 1.6,
  },

  panel: {
    marginTop: '2.4rem',
    textAlign: 'left',
    padding: '1.5rem',
    borderRadius: 14,
    background: '#0b1828',
    border: '1px solid rgba(85,199,255,0.14)',
  },

  form: {
    display: 'grid',
    gap: '0.9rem',
  },

  field: {
    display: 'grid',
    gap: '0.35rem',
  },

  label: {
    color: '#d7ebff',
    fontSize: '0.82rem',
    fontWeight: 700,
  },

  input: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid rgba(85,199,255,0.2)',
    background: 'rgba(255,255,255,0.04)',
    color: '#ffffff',
    borderRadius: 10,
    padding: '0.75rem 0.85rem',
    fontSize: '0.98rem',
    outline: 'none',
  },

  invitationCodeInput: {
    background: 'rgba(85,199,255,0.08)',
    border: '1px solid rgba(85,199,255,0.34)',
    color: '#d7ebff',
    cursor: 'default',
  },

  helper: {
    color: '#8db7e6',
    fontSize: '0.82rem',
    lineHeight: 1.4,
  },

  error: {
    color: '#ffb3b3',
    background: 'rgba(255,77,77,0.08)',
    border: '1px solid rgba(255,77,77,0.18)',
    borderRadius: 10,
    padding: '0.75rem',
  },

  primaryButton: {
    border: 'none',
    background: '#55c7ff',
    color: '#04111f',
    borderRadius: 10,
    padding: '0.75rem 1rem',
    fontWeight: 800,
    cursor: 'pointer',
    marginTop: '0.3rem',
    width: '100%',
  },

  footer: {
    marginTop: '1.2rem',
    color: '#8fa8c5',
    fontSize: '0.95rem',
  },

  footerLink: {
    border: 'none',
    background: 'transparent',
    color: '#55c7ff',
    fontWeight: 800,
    cursor: 'pointer',
    padding: 0,
  },
};